import { useCallback, useMemo } from 'react';

import { IconMap } from '../../../shared/sprite';
import { DropdownItem } from '../../../shared/ui/Dropdown';
import { Page } from '@distribute/shared/types';
import { EDITOR_PAGE_ROUTE } from '../../../entities/history';
import { useWindowWidth } from '@distribute/frontend/utils';
import { useDispatch, useSelector } from 'react-redux';

import { createNotification } from '../../../features/snackbar';
import { snackbarModel } from '../../../features/snackbar';

import { pagesModel, usePagePermissions } from '../../../features/pages';
import { createPageQueryRandomValue } from '../../../features/pages/lib';
import { getFullPageUrl } from '../../../shared/lib';
import { teamsModel, useTeamPermissions } from '../../../features/teams';
import { customDomainsModel } from '../../../features/custom-domains';
import { generatePath } from 'react-router-dom';
import { OPENED_PAGE_PARAM } from '../../new-editor';

import { navigate } from '../../../processes/navigation';
import { foldersModel, useFolderPermissions } from '../../../features/folders';
import { useOpenEditor } from './use-open-editor';

type Params = {
  page: Page;
  onSharePage: (id: string) => void;
  onMoveToFolder: (id: string) => void;
  onDelete: (id: string) => void;
};

export const usePageDropdownActions = ({
  page,
  onSharePage,
  onMoveToFolder,
  onDelete,
}: Params) => {
  const { sequenceNumber, id, folderId, pageSlug, published } = page;

  const currentFolder = useSelector(foldersModel.selectors.selectCurrentFolder);

  const { handlePageEditorClick } = useOpenEditor(sequenceNumber);

  const folderPermissions = useFolderPermissions(currentFolder);
  const pagePermissions = usePagePermissions(page);

  const { isGuest } = useTeamPermissions();

  const { domain } = useSelector(
    teamsModel.selectors.selectCurrentTeamWithError
  );
  const customDomain = useSelector(
    customDomainsModel.selectors.selectVerifiedCustomDomain
  );

  const pageUrl = getFullPageUrl({
    slug: pageSlug,
    domain,
    customDomain,
  });

  const { isMobile } = useWindowWidth();

  const dispatch = useDispatch();

  const onDuplicate = useCallback(() => {
    dispatch(
      pagesModel.actions.duplicatePage({
        id,
        folderId,
        source: 'workspace',
      })
    );
  }, [dispatch, folderId, id]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(`${pageUrl}?${createPageQueryRandomValue()}`);
    dispatch(
      snackbarModel.actions.addNotificationAction(
        createNotification(
          'success',
          'The link has been copied to your clipboard'
        )
      )
    );
  }, [dispatch, pageUrl]);

  const onOpenAnalytics = useCallback(() => {
    const path = `${generatePath(EDITOR_PAGE_ROUTE, {
      sequenceNumber,
    })}?${OPENED_PAGE_PARAM}=analytics`;

    dispatch(navigate({ to: path }));
  }, [sequenceNumber, dispatch]);

  const teamUsers = useSelector(teamsModel.selectors.selectCurrentTeamMembers);
  const teamUsersInviteAccepted = useMemo(
    () => teamUsers.filter((user) => !user.isWaitingForInviteAcceptance),
    [teamUsers]
  );

  const onRequestToEdit = useCallback(() => {
    dispatch(pagesModel.actions.requestToEditPage({ pageId: id }));
  }, [dispatch, id]);

  const pageOptions = useMemo<DropdownItem[]>(
    () =>
      [
        {
          id: 'Editor',
          label: 'Editor',
          onClick: handlePageEditorClick,
          iconName: IconMap.MenuEdit,
          iconWidth: 16,
          style: 'sm:hidden',
          isShow: pagePermissions.isCanEditDocumentContent,
        },
        {
          id: 'Preview',
          label: 'Preview',
          onClick: handlePageEditorClick,
          iconName: IconMap.Eye,
          iconWidth: 16,
          isShow: !pagePermissions.isCanEditDocumentContent,
        },
        {
          id: 'Analytics',
          label: 'Analytics',
          onClick: onOpenAnalytics,
          iconName: IconMap.AnalyticsIcon,
          isSeparatedFromTop: isMobile,
          iconWidth: 16,
          isShow: published && !isGuest,
        },
        {
          id: 'Ask to edit',
          label: 'Ask to edit',
          onClick: onRequestToEdit,
          iconName: IconMap.Edit05,
          iconWidth: 16,
          isShow: !pagePermissions.isCanEditDocumentContent,
        },
        {
          id: 'Share',
          label: 'Share',
          onClick: () => onSharePage(id),
          iconName: IconMap.UserPlus,
          iconWidth: 16,
          isShow:
            ((teamUsersInviteAccepted.length > 1 &&
              (pagePermissions.isCanInviteMembers ||
                pagePermissions.isCanManageAccess)) ||
              pagePermissions.isCanPublishPage) &&
            !isGuest,
          isSeparatedFromTop: true,
          isSeparatedFromBottom: true,
        },
        {
          id: 'Duplicate',
          label: 'Duplicate',
          onClick: onDuplicate,
          iconName: IconMap.MenuCopy,
          isShow: folderPermissions.isCanDuplicatePages,
          iconWidth: 16,
        },
        {
          id: 'CopyLink',
          label: 'Copy link',
          onClick: handleCopyLink,
          iconName: IconMap.Link,
          iconWidth: 16,
        },
        {
          id: 'MoveToFolder',
          label: 'Move to folder',
          onClick: () => onMoveToFolder(id),
          iconName: IconMap.MenuFolder,
          iconWidth: 16,
          isShow: folderPermissions.isCanMovePagesToAnotherFolder,
        },
        {
          id: 'Delete',
          label: 'Delete',
          onClick: () => onDelete(id),
          iconName: IconMap.Delete,
          iconWidth: 16,
          isSeparatedFromTop: true,
          isShow: pagePermissions.isCanDelete,
        },
      ].filter((i) => i.isShow !== false),
    [
      handleCopyLink,
      handlePageEditorClick,
      onDelete,
      onDuplicate,
      onMoveToFolder,
      onOpenAnalytics,
      onSharePage,
      onRequestToEdit,
      id,
      published,
      isMobile,
      folderPermissions.isCanDuplicatePages,
      folderPermissions.isCanMovePagesToAnotherFolder,
      pagePermissions.isCanDelete,
      pagePermissions.isCanManageAccess,
      pagePermissions.isCanInviteMembers,
      pagePermissions.isCanPublishPage,
      pagePermissions.isCanEditDocumentContent,
      teamUsersInviteAccepted,
      isGuest,
    ]
  );

  return useMemo<DropdownItem[]>(
    () =>
      published
        ? pageOptions
        : pageOptions.filter((item) => item.id !== 'CopyLink'),
    [pageOptions, published]
  );
};
